import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { getProduct, money } from "@/lib/products";
import { Reveal } from "./Reveal";
import { useStock } from "@/context/StockContext";
import { NotifyMe } from "./NotifyMe";

const TONES = [
  { id: "fair", label: "Fair", skin: "#F3D9C8", picks: [["glasshouse-gloss", "Petal Silk"], ["melt-lip-oil", "Crystal Halo"]] },
  { id: "light", label: "Light", skin: "#E6BFA3", picks: [["glasshouse-gloss", "Bare Honey"], ["melt-lip-oil", "Rosewood"]] },
  { id: "medium", label: "Medium", skin: "#C99476", picks: [["glasshouse-gloss", "Bare Honey"], ["melt-lip-oil", "Honey Dew"]] },
  { id: "tan", label: "Tan", skin: "#A26E50", picks: [["glasshouse-gloss", "Mocha Glaze"], ["melt-lip-oil", "Rosewood"]] },
  { id: "deep", label: "Deep", skin: "#6B4330", picks: [["glasshouse-gloss", "Mocha Glaze"], ["melt-lip-oil", "Honey Dew"]] },
  { id: "rich", label: "Rich", skin: "#45291D", picks: [["glasshouse-gloss", "Berry Velvet"], ["melt-lip-oil", "Honey Dew"]] },
];

const Lips = ({ skin, color }) => (
  <svg viewBox="0 0 200 120" className="w-full max-w-[340px]" data-testid="shade-lip-preview">
    <rect width="200" height="120" rx="60" fill={skin} style={{ transition: "fill .6s" }} />
    <path d="M40 62c18-22 38-30 50-22 6 4 14 4 20 0 12-8 32 0 50 22-24 22-44 32-60 32S64 84 40 62z" fill={color} style={{ transition: "fill .6s" }} />
    <path d="M40 62c30 4 90 4 120 0" stroke="rgba(0,0,0,.25)" strokeWidth="2" fill="none" />
    <ellipse cx="88" cy="74" rx="16" ry="5" fill="rgba(255,255,255,.45)" />
  </svg>
);

export function ShadeStudio() {
  const [tone, setTone] = useState(TONES[3]);
  const [pick, setPick] = useState(0);
  const { add } = useCart();
  const { isSoldOut } = useStock();
  const [pid, shadeName] = tone.picks[pick];
  const product = getProduct(pid);
  const shade = product?.shades.find((s) => s.name === shadeName);

  return (
    <section id="shades" className="relative py-24 lg:py-36 bg-[#F9ECE8] overflow-hidden" data-testid="shade-studio-section">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <Reveal className="lg:col-span-5">
          <p className="overline text-[#6E504A]">Shade Studio</p>
          <h2 className="font-display text-5xl sm:text-6xl mt-5 leading-[0.95]">Find the shade that <span className="italic text-[#D88B94]">finds you.</span></h2>
          <p className="mt-6 text-[#6E504A] max-w-md leading-relaxed">Choose the tone closest to yours and we'll suggest a gloss and an oil to start with. Every shade is designed to flatter more than one skin tone — these are just our favourite pairings.</p>
          <div className="mt-10 flex flex-wrap gap-3">
            {TONES.map((t) => (
              <button key={t.id} onClick={() => { setTone(t); setPick(0); }} data-testid={`tone-${t.id}`}
                className={`flex items-center gap-2 rounded-full pl-1.5 pr-4 h-11 border text-sm transition-colors duration-200 ${tone.id === t.id ? "bg-[#281715] text-[#FAF7F2] border-[#281715]" : "border-[#E6D7CC] bg-[#FAF7F2] hover:border-[#4A2E2B]"}`}>
                <span className="w-8 h-8 rounded-full" style={{ background: t.skin }} />{t.label}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
          <div className="relative rounded-[32px] bg-[#FAF7F2] border border-[#E6D7CC] p-8 flex flex-col items-center justify-center min-h-[320px]">
            <Lips skin={tone.skin} color={shade?.hex ?? "#C99A7E"} />
            <p className="overline text-[#6E504A] mt-8">{tone.label} skin · preview</p>
          </div>
          <div className="rounded-[32px] bg-[#281715] text-[#FAF7F2] p-8 flex flex-col">
            <div className="flex gap-2">
              {tone.picks.map(([p, s], i) => (
                <button key={p} onClick={() => setPick(i)} data-testid={`tone-pick-${i}`}
                  className={`overline px-3 py-2 rounded-full transition-colors duration-200 ${pick === i ? "bg-[#EBB1B6] text-[#281715]" : "text-[#D9C4BC] border border-[#6E504A]"}`}>
                  {i === 0 ? "Gloss" : "Oil"}
                </button>
              ))}
            </div>
            <motion.div key={`${tone.id}-${pick}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-auto pt-10">
              <span className="block w-16 h-16 rounded-full ring-4 ring-[#4A2E2B]" style={{ background: shade?.hex }} />
              <p className="font-display text-4xl mt-6" data-testid="shade-recommendation-name">{shadeName}</p>
              <p className="text-[#D9C4BC] mt-2 text-sm">{product?.name} · {shade?.note}</p>
              {isSoldOut(pid, shadeName) ? <div className="mt-8"><NotifyMe key={`${pid}-${shadeName}`} productId={pid} shade={shadeName} dark /></div> : (
              <button onClick={() => { add(pid, shadeName); toast.success(`${product?.name} · ${shadeName} added`); }}
                data-testid="shade-add-btn"
                className="mt-8 w-full rounded-full bg-[#FAF7F2] text-[#281715] h-12 text-sm font-medium hover:bg-[#EBB1B6] transition-colors duration-200">
                Add to bag — {money(product?.price)}
              </button>
              )}
            </motion.div>
          </div>
          <div className="sm:col-span-2 relative h-40 sm:h-48 rounded-[32px] overflow-hidden">
            <img src="/images/swatches.jpg" alt="LUMI gloss swatches in mocha, nude, rose and berry" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          </div>
        </div>
      </div>
    </section>
  );
}
